import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { generarLiquidacionPDF } from "../components/LiquidacionPDF";
import { fetchUsersByUserIds, tolerantId } from "./firestoreHelpers";
import { leerGastosPorId } from "./consultasEdificio";
import { getOccupanciesForBuilding } from "./occupancies";
import { resolveCierreWindow } from "./cierrePeriod";
import { calculateFundBalances } from "./fundBalances";
import { esPagoDeAlquiler } from "./unidadesEdificio";

/**
 * Fichas `users` de los titulares de los pagos del cierre: los nombres de la
 * "Distribución por Unidad". Con las reglas 0D (C10) el staff las lee con el
 * edificio fijado (fetchUsersByUserIds: accessBids array-contains), y así
 * entran también los ocupantes salientes que ya no están en la unidad.
 *
 * El PDF les llega a todos los residentes: si la lectura falla, o no vuelve
 * NINGUNA ficha habiendo titulares, se lanza en vez de armarlo con "Sin
 * nombre" (al aprobar, el cierre se publica sin PDF y la pantalla lo avisa).
 *
 * @param {Array<Record<string, any>>} pagos - Pagos del cierre
 * @param {number} buildingId - Edificio del cierre
 * @returns {Promise<Array<Record<string, any>>>}
 */
async function fichasDeLosPagos(pagos, buildingId) {
  const ids = [
    ...new Set(
      pagos
        .map((p) => p.userid)
        .filter((u) => u != null && u !== "" && !String(u).startsWith("unit_"))
        .map(String)
    ),
  ];
  if (ids.length === 0) return [];
  const fichas = await fetchUsersByUserIds(ids, buildingId);
  if (fichas.length === 0) {
    throw new Error(
      `No se pudo leer ninguna ficha de los ${ids.length} residente(s) del cierre: la liquidación no se arma sin sus nombres.`
    );
  }
  return fichas;
}

/**
 * Arma el PDF de liquidación de un cierre de mes (el mismo que baja el admin
 * en Cierre de mes). Compartido por:
 *   - CierreMes (botón PDF → output "download")
 *   - CierreRevision (al APROBAR → output "blob": se sube a Storage y la Cloud
 *     Function lo manda por WhatsApp a cada residente con la nueva_expensa).
 *
 * Unidades y fichas se buscan SIEMPRE frescas (ver nota abajo);
 * selectedBuilding es opcional.
 *
 * @returns {Promise<{blob?: Blob, fileName: string}>}
 */
export async function buildLiquidacionPdf({
  buildingId: rawBuildingId,
  cierre,
  selectedBuilding = null,
  allUsers = null,
  allApartments = null,
  output = "download",
}) {
  const buildingId = Number(rawBuildingId);
  // SIEMPRE datos frescos de unidades (y, más abajo, de las fichas de los
  // titulares de los pagos). Antes se usaban los que la pantalla cargaba en
  // segundo plano: si el PDF se pedía antes de que terminaran (o recién
  // cambiado el edificio) llegaban vacíos/viejos y la "Distribución por Unidad"
  // salía con "Sin nombre" y unidad "-" (a veces sí, a veces no).
  // allUsers/allApartments se ignoran a propósito.
  void allUsers; void allApartments;
  const aptSnap = await getDocs(query(collection(db, "apartments"), where("building", "in", tolerantId(buildingId))));
  allApartments = aptSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // Obtener datos del edificio
    const buildingSnap = await getDocs(
      query(collection(db, "building"), where("buildingid", "in", tolerantId(buildingId)))
    );
    const buildingData = buildingSnap.docs[0]?.data() || {};

    // Obtener payments generados para este cierre
    const paymentsSnap = await getDocs(
      query(
        collection(db, "payments"),
        where("buildingid", "in", tolerantId(buildingId)),
        where("month", "==", cierre.month),
        where("year", "==", cierre.year)
      )
    );
    const paymentsData = paymentsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    // Fichas de los titulares de esos pagos (C10; lanza si no se pueden leer).
    allUsers = await fichasDeLosPagos(paymentsData, buildingId);

    // Gastos del cierre: si el cierre guardó la lista EXACTA que liquidó
    // (liquidatedExpenseIds, incluye los pagados por un fondo) se usa esa.
    // Cierres viejos sin la lista → reconstrucción aproximada (abajo).
    // Por id se lee con getDoc (leerGastosPorId): una consulta por __name__ no
    // fija el edificio y las reglas 0D la niegan entera.
    let expenses = [];
    const liquidatedIds = Array.isArray(cierre.liquidatedExpenseIds) ? cierre.liquidatedExpenseIds : [];
    if (liquidatedIds.length > 0) {
      expenses.push(...(await leerGastosPorId(db, liquidatedIds)));
    } else {
      // === ESTRATEGIA ROBUSTA PARA OBTENER GASTOS ===
      // 1. Intentar desde expenseIds de los payments
      const allExpenseIds = new Set();
      paymentsData.forEach(p => {
        if (p.expenseIds && Array.isArray(p.expenseIds)) {
          p.expenseIds.forEach(id => allExpenseIds.add(id));
        }
      });

      // 2. TAMBIEN buscar en expensesapartments (fuente más confiable)
      const expAptSnap = await getDocs(
        query(
          collection(db, "expensesapartments"),
          where("buildingid", "in", tolerantId(buildingId)),
          where("month", "==", cierre.month),
          where("year", "==", cierre.year)
        )
      );
      expAptSnap.docs.forEach(d => {
        const data = d.data();
        if (data.expenseId) {
          allExpenseIds.add(data.expenseId);
        }
      });

      // 3. Obtener los gastos reales usando los IDs recopilados (getDoc por id)
      if (allExpenseIds.size > 0) {
        expenses.push(...(await leerGastosPorId(db, Array.from(allExpenseIds))));
      }

      // 4. Fallback: si aun no hay expenses, buscar por fecha de pago
      if (expenses.length === 0) {
        const expensesSnap = await getDocs(
          query(collection(db, "expenses"), where("buildingid", "in", tolerantId(buildingId)))
        );
        const allExpenses = expensesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const startDate = new Date(cierre.year, cierre.month - 1, 1);
        const endDate = new Date(cierre.year, cierre.month, 0, 23, 59, 59);
        expenses = allExpenses.filter(exp => {
          if (!exp.paid) return false;
          const dateField = exp.paidDate;
          if (!dateField) return false;
          let date;
          if (typeof dateField === "string") {
            const parts = dateField.split("-");
            date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
          } else if (dateField?.toDate) date = dateField.toDate();
          else if (dateField?.seconds) date = new Date(dateField.seconds * 1000);
          else date = new Date(dateField);
          return date >= startDate && date <= endDate;
        });
      }

      // 5. Completar con gastos de "obra" o "incidencia" cuya invoiceDate caiga en el mes del cierre.
      // Los gastos con categoría "obra" se excluyen del reparto a unidades en el cierre (linea ~609),
      // por lo que no figuran en expensesapartments ni en expenseIds de los payments. Sin embargo,
      // la sección "Detalle de Gastos del Mes" del PDF debe mostrarlos — especialmente ahora que
      // pueden estar asociados a fondos personalizados via sourceFund (FIN-019).
      try {
        const extraSnap = await getDocs(
          query(collection(db, "expenses"), where("buildingid", "in", tolerantId(buildingId)))
        );
        const existingIds = new Set(expenses.map(e => e.id));
        const startDate = new Date(cierre.year, cierre.month - 1, 1);
        const endDate = new Date(cierre.year, cierre.month, 0, 23, 59, 59);
        const parseInvDate = (dateField) => {
          if (!dateField) return null;
          let date;
          if (typeof dateField === "string") {
            const parts = dateField.split("-");
            date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
          } else if (dateField?.toDate) date = dateField.toDate();
          else if (dateField?.seconds) date = new Date(dateField.seconds * 1000);
          else date = new Date(dateField);
          return isNaN(date.getTime()) ? null : date;
        };
        extraSnap.docs.forEach(d => {
          if (existingIds.has(d.id)) return;
          const data = { id: d.id, ...d.data() };
          const cat = data.expenseCategory || "gastos_comunes";
          // Solo obras/incidencias adicionales (los gastos_comunes ya vienen por expenseIds/expensesapartments)
          if (cat !== "obra" && cat !== "incidencia") return;
          // Por fecha de factura O de pago en el mes (el cierre liquida por fecha
          // de pago: un gasto facturado hace meses y pagado ahora también va).
          const inv = parseInvDate(data.invoiceDate);
          const pd = data.paid ? parseInvDate(data.paidDate) : null;
          const enMes = (d) => d && d >= startDate && d <= endDate;
          if (enMes(inv) || enMes(pd)) {
            expenses.push(data);
          }
        });
      } catch (err) {
        console.error("Error agregando obras/incidencias al detalle del PDF:", err);
      }
    }

    // === PROVEEDORES: mapear por AMBOS: supplierId y doc.id ===
    const suppliersSnap = await getDocs(collection(db, "supplier"));
    const suppliersMap = {};
    suppliersSnap.docs.forEach(d => {
      const data = d.data();
      const nombre = data.name || data.nombre || "Sin nombre";
      // Mapear por el campo supplierId
      if (data.supplierId) {
        suppliersMap[String(data.supplierId)] = nombre;
      }
      // Mapear TAMBIEN por el document ID de Firestore
      suppliersMap[d.id] = nombre;
    });

    // Enriquecer expenses con nombre del proveedor
    const enrichedExpenses = expenses.map(exp => ({
      ...exp,
      supplierName: suppliersMap[String(exp.supplierid)] || suppliersMap[String(exp.supplierId)] || "-",
    }));

    // Tenencias históricas del edificio: si el titular de un pago ya NO es
    // ocupante actual de ninguna unidad (se fue / cambió el ocupante), su
    // deuda vieja igual entra al cierre. Sin este fallback esas filas salían
    // como unidad "-" sin contexto; con occupancies las asignamos a la
    // última unidad que ocupó, marcadas como (saliente).
    let buildingOccs = [];
    try {
      buildingOccs = await getOccupanciesForBuilding(buildingId);
    } catch (err) {
      console.warn("No se pudieron cargar occupancies para el PDF:", err?.message);
    }

    // === ENRIQUECER PAYMENTS con nombre y unidad ===
    const enrichedPayments = paymentsData.map(p => {
      const user = allUsers.find(u => String(u.userid) === String(p.userid));
      // Match por owner/tenant + synthetic ID (unit_XXX)
      let apt = allApartments.find(a =>
        String(a.owner) === String(p.userid) ||
        String(a.tenant) === String(p.userid) ||
        p.userid === `unit_${a.id}`
      );
      const isSynthetic = p.userid?.startsWith("unit_");
      let isOwner = apt && (String(apt.owner) === String(p.userid) || isSynthetic);
      let isTenant = apt && apt.tenant && String(apt.tenant) === String(p.userid);
      let saliente = false;

      // Fallback: ocupante saliente → última tenencia registrada en el edificio.
      if (!apt) {
        const lastOcc = buildingOccs
          .filter((o) => String(o.userid) === String(p.userid) && o.apartmentId)
          .sort((a, b) => String(b.dateFrom || "").localeCompare(String(a.dateFrom || "")))[0];
        if (lastOcc) {
          apt = allApartments.find((a) => String(a.id) === String(lastOcc.apartmentId));
          if (apt) {
            saliente = true;
            isOwner = lastOcc.role !== "tenant";
            isTenant = lastOcc.role === "tenant";
          }
        }
      }

      const hasBothRoles = apt && apt.owner && apt.tenant && String(apt.tenant).trim() !== "";
      // Rol: P (propietario) o I (inquilino). Mostrar letra si la unidad tiene
      // ambos roles, y siempre para salientes (aclara qué rol tenía).
      const role = (hasBothRoles || saliente) ? (isTenant ? "I" : "P") : "P";
      const baseName = user?.name || user?.NombreCompleto || (isSynthetic ? `Unidad ${apt?.number || apt?.name || "s/n"}` : "Sin nombre");
      return {
        ...p,
        userName: saliente ? `${baseName} (saliente)` : baseName,
        unitNumber: apt?.number || apt?.name || "-",
        _isOwner: isOwner,
        _role: role,
        _hasBothRoles: hasBothRoles || saliente,
        _saliente: saliente,
      };
    });

    // Ordenar: por número de unidad (numérico), propietario antes que inquilino
    enrichedPayments.sort((a, b) => {
      const cmp = (a.unitNumber || "").localeCompare(b.unitNumber || "", undefined, { numeric: true });
      if (cmp !== 0) return cmp;
      return (b._isOwner ? 1 : 0) - (a._isOwner ? 1 : 0);
    });

    // Obtener nombre del admin. La consulta fija el edificio (reglas 0D: el
    // staff lee los vínculos admin de SU edificio) y es best-effort: sin el
    // nombre, el PDF sale igual con "Administrador".
    let adminName = "Administrador";
    if (selectedBuilding?.adminid) {
      try {
        const adminSnap = await getDocs(
          query(
            collection(db, "admin"),
            where("adminid", "==", selectedBuilding.adminid),
            where("buildingid", "in", tolerantId(buildingId))
          )
        );
        if (!adminSnap.empty) {
          adminName = adminSnap.docs[0].data().name || "Administrador";
        }
      } catch (err) {
        console.warn("No se pudo leer el administrador para el PDF:", err?.code || err?.message);
      }
    }

    // === RESUMEN POR FONDOS (saldo anterior / cobranza / gastos / créditos /
    // débitos / saldo actual). Necesita el HISTÓRICO completo del edificio
    // (payments/expenses/multas/ingresos) porque el saldo anterior se deriva
    // de todos los períodos previos — la misma cuenta que el Informe de
    // Gastos Comunes (calculateFundBalances). Best-effort: si algo falla,
    // el PDF sale sin esa sección.
    let fundBalances = null;
    let fundBalancesUSD = null;
    try {
      const buildingForFunds = { ...buildingData, ...selectedBuilding };
      const [allPaySnap, allExpSnap, multasSnap, incomesSnap] = await Promise.all([
        getDocs(query(collection(db, "payments"), where("buildingid", "in", tolerantId(buildingId)))),
        getDocs(query(collection(db, "expenses"), where("buildingid", "in", tolerantId(buildingId)))),
        getDocs(query(collection(db, "multas"), where("buildingid", "in", tolerantId(buildingId)))),
        getDocs(query(collection(db, "buildingincomes"), where("buildingid", "in", tolerantId(buildingId)))),
      ]);
      const allPaymentsFull = allPaySnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const allExpensesFull = allExpSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const allMultas = multasSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const allIncomes = incomesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Ventana del período: la persistida en el cierre (periodStart/periodEnd)
      // tiene prioridad; si no existe (cierres viejos), se resuelve por diaCorte.
      const diaCorte = buildingForFunds?.diaCorte ?? null;
      const win = resolveCierreWindow(cierre.month, cierre.year, { diaCorte, cierreDoc: cierre });
      const useWindow = Boolean((cierre.periodStart && cierre.periodEnd) || diaCorte);
      const fundParams = {
        expenses: allExpensesFull,
        payments: allPaymentsFull,
        multas: allMultas,
        incomes: allIncomes,
        building: buildingForFunds,
        year: cierre.year,
        month: cierre.month,
        periodStart: useWindow ? win.start : null,
        periodEnd: useWindow ? win.end : null,
      };
      fundBalances = calculateFundBalances({ ...fundParams, currency: "peso" });
      fundBalancesUSD = calculateFundBalances({ ...fundParams, currency: "dolar" });
    } catch (err) {
      console.error("Resumen por fondos para el PDF falló (se omite la sección):", err);
    }

    return await generarLiquidacionPDF({
      building: { ...buildingData, ...selectedBuilding },
      month: cierre.month,
      year: cierre.year,
      expenses: enrichedExpenses,
      apartments: allApartments,
      // El alquiler de una unidad del edificio no va en "Distribución por Unidad"
      // (expondría al inquilino ante todo el edificio): figura como crédito del fondo.
      payments: enrichedPayments.filter((p) => !esPagoDeAlquiler(p)),
      maturity: cierre.maturity || "",
      adminName,
      fundBalances,
      fundBalancesUSD,
      output,
    });
}
